import { AppSettings, AppSettingsSchema, defaultSettings } from './schema';

type SettingsOverrides = {
  ha?: Partial<AppSettings['ha']>;
  assistant?: Partial<AppSettings['assistant']>;
  theme?: Partial<AppSettings['theme']>;
  generator?: Partial<AppSettings['generator']>;
  mockRegistry?: boolean;
};

function mergeSection<T extends Record<string, unknown>>(
  base: T,
  override: Partial<T> | undefined,
): T {
  if (!override) return base;
  return { ...base, ...override };
}

function mergeWithDefaults(overrides: SettingsOverrides): AppSettings {
  return {
    ha: mergeSection(defaultSettings.ha, overrides.ha),
    assistant: mergeSection(defaultSettings.assistant, overrides.assistant),
    theme: mergeSection(defaultSettings.theme, overrides.theme),
    generator: mergeSection(defaultSettings.generator, overrides.generator),
    mockRegistry: overrides.mockRegistry ?? defaultSettings.mockRegistry,
  };
}

function resolveSameOrigin(v: string | undefined): string | undefined {
  if (!v) return v;
  if (v === '__SAME_ORIGIN__') return window.location.origin;
  if (v === '__SAME_ORIGIN_WS__') {
    const p = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${p}//${window.location.host}/api/websocket`;
  }
  return v;
}

export function loadSettings(): AppSettings {
  const raw =
    (window as unknown as { __RSC_DASH_SETTINGS?: SettingsOverrides })
      .__RSC_DASH_SETTINGS ?? {};

  // Resolve __SAME_ORIGIN__ sentinels + default missing url/websocket to current origin.
  const originUrl = window.location.origin;
  const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const originWs = `${wsProto}//${window.location.host}/api/websocket`;
  const overrides: SettingsOverrides = {
    ...raw,
    ha: raw.ha
      ? {
          ...(raw.ha ?? {}),
          url: resolveSameOrigin(raw.ha?.url as string | undefined) ?? originUrl,
          websocket:
            raw.ha?.websocket === '__SAME_ORIGIN__' || !raw.ha?.websocket
              ? originWs
              : resolveSameOrigin(raw.ha.websocket as string | undefined),
          fallbackUrl: resolveSameOrigin(raw.ha?.fallbackUrl as string | undefined),
          fallbackWebsocket: resolveSameOrigin(
            raw.ha?.fallbackWebsocket as string | undefined,
          ),
        }
      : undefined,
  };

  const candidate = mergeWithDefaults(overrides);
  const result = AppSettingsSchema.safeParse(candidate);

  if (result.success) {
    return result.data;
  }

  for (const issue of result.error.issues) {
    console.warn(
      `[settings] invalid override at ${issue.path.join('.') || '<root>'}: ${issue.message}`,
    );
  }
  return defaultSettings;
}
